<template>
  <div class="flex overflow-hidden" style="height: 75vh;">
    <!-- Left Panel: Template List -->
    <div class="w-72 border-r border-gray-200 flex flex-col bg-white">
      <div class="p-4 border-b border-gray-200">
        <h2 class="font-bold text-lg mb-3">Report Templates</h2>
        <button @click="startNewTemplate"
          class="w-full py-2 px-3 bg-custom-yellow text-white rounded text-sm font-medium hover:bg-orange-600">
          + New Template
        </button>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div v-if="!templates.length" class="p-4 text-sm text-gray-500 text-center mt-8">
          No templates yet.<br/>Click "New Template" to start.
        </div>
        <div v-for="t in templates" :key="t.id"
          class="p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition"
          :class="{ 'bg-yellow-50 border-l-4 border-l-custom-yellow': selectedTemplate?.id === t.id }"
          @click="selectTemplate(t)">
          <div class="font-medium text-sm">{{ t.name }}</div>
          <div class="text-xs text-gray-500 mt-0.5">{{ t.sections.length }} section(s)</div>
          <div v-if="t.imaging_type" class="text-xs text-blue-600 mt-0.5">{{ t.imaging_type }}</div>
        </div>
      </div>
    </div>

    <!-- Right Panel: Template Detail -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- No selection -->
      <div v-if="!selectedTemplate && !isCreating" class="flex items-center justify-center h-full text-gray-400">
        Select a template or create a new one
      </div>

      <!-- Template Form -->
      <div v-else>
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-bold">{{ isCreating ? 'New Template' : 'Edit Template' }}</h3>
          <div class="flex gap-2">
            <button v-if="!isCreating" @click="confirmDelete"
              class="px-4 py-2 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50">
              Delete
            </button>
          </div>
        </div>

        <!-- Name & Description -->
        <div class="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div class="mb-3">
            <label class="text-sm font-medium text-gray-700">Template Name *</label>
            <input v-model="form.name" type="text" placeholder="e.g. Lumbar Spine X-Ray"
              class="mt-1 w-full border border-gray-300 rounded p-2 text-sm bg-gray-50" />
          </div>
          <div class="mb-3">
            <label class="text-sm font-medium text-gray-700">Description</label>
            <textarea v-model="form.description" rows="2" placeholder="Optional description..."
              class="mt-1 w-full border border-gray-300 rounded p-2 text-sm bg-gray-50" />
          </div>
          <div class="flex items-center gap-3">
            <label class="text-sm font-medium text-gray-700">Active</label>
            <input type="checkbox" v-model="form.is_active" class="w-4 h-4 accent-yellow-400" />
          </div>
          <div class="mt-4 flex justify-end">
            <button @click="saveTemplate" :disabled="saving"
              class="px-5 py-2 bg-custom-yellow text-white rounded text-sm hover:bg-orange-600 disabled:opacity-50">
              {{ saving ? 'Saving...' : (isCreating ? 'Create Template' : 'Save Changes') }}
            </button>
          </div>
        </div>

        <!-- Sections (only after template is saved) -->
        <div v-if="!isCreating" class="bg-white rounded-lg border border-gray-200 p-4">
          <h4 class="font-semibold text-sm mb-3">Sections</h4>
          <p class="text-xs text-gray-500 mb-4">
            Sections appear as individual text fields when a radiologist applies this template.
          </p>

          <!-- Existing sections -->
          <div v-for="section in selectedTemplate.sections" :key="section.id"
            class="flex items-start gap-3 mb-3 p-3 bg-gray-50 rounded border border-gray-200">
            <div class="flex-1">
              <div class="font-medium text-sm">{{ section.title }}</div>
              <div v-if="section.placeholder_text" class="text-xs text-gray-500 mt-0.5 italic">
                {{ section.placeholder_text }}
              </div>
            </div>
            <button @click="removeSection(section)"
              class="text-red-400 hover:text-red-600 text-xs px-2 py-1 border border-red-200 rounded">
              Remove
            </button>
          </div>

          <div v-if="!selectedTemplate.sections.length" class="text-sm text-gray-400 text-center py-4">
            No sections yet. Add sections below.
          </div>

          <!-- Add Section Form -->
          <div class="mt-4 border-t border-gray-100 pt-4">
            <h5 class="text-sm font-medium mb-2">Add Section</h5>
            <div class="flex flex-col gap-2">
              <input v-model="newSection.title" type="text" placeholder="Section title (e.g. Findings)"
                class="border border-gray-300 rounded p-2 text-sm bg-gray-50" />
              <input v-model="newSection.placeholder_text" type="text"
                placeholder="Hint text shown to radiologist (optional)"
                class="border border-gray-300 rounded p-2 text-sm bg-gray-50" />
              <div class="flex justify-end">
                <button @click="addSection" :disabled="!newSection.title.trim() || addingSection"
                  class="px-4 py-2 bg-custom-yellow text-white rounded text-sm hover:bg-orange-600 disabled:opacity-40">
                  {{ addingSection ? 'Adding...' : 'Add Section' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  data() {
    return {
      templates: [],
      selectedTemplate: null,
      isCreating: false,
      saving: false,
      addingSection: false,
      form: { name: '', description: '', is_active: true },
      newSection: { title: '', placeholder_text: '' },
    };
  },
  async created() {
    await this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      const res = await apiClient.listRadiologyTemplates();
      if (res.success) this.templates = res.data;
    },
    selectTemplate(t) {
      this.selectedTemplate = t;
      this.isCreating = false;
      this.form = { name: t.name, description: t.description || '', is_active: t.is_active };
    },
    startNewTemplate() {
      this.selectedTemplate = null;
      this.isCreating = true;
      this.form = { name: '', description: '', is_active: true };
    },
    async saveTemplate() {
      if (!this.form.name.trim()) return this.$toast.error("Template name is required");
      this.saving = true;
      try {
        if (this.isCreating) {
          const res = await apiClient.createRadiologyTemplate(this.form);
          if (res.success) {
            this.$toast.success("Template created");
            await this.loadTemplates();
            const created = this.templates.find(t => t.id === res.data.id);
            if (created) this.selectTemplate(created);
            else { this.selectedTemplate = res.data; this.isCreating = false; }
          } else {
            this.$toast.error(res.message || "Failed to create template");
          }
        } else {
          const res = await apiClient.updateRadiologyTemplate(this.selectedTemplate.id, this.form);
          if (res.success) {
            this.$toast.success("Template saved");
            await this.loadTemplates();
            const updated = this.templates.find(t => t.id === this.selectedTemplate.id);
            if (updated) this.selectTemplate(updated);
          } else {
            this.$toast.error(res.message || "Failed to save template");
          }
        }
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete() {
      if (!confirm(`Delete template "${this.selectedTemplate.name}"?`)) return;
      const res = await apiClient.deleteRadiologyTemplate(this.selectedTemplate.id);
      if (res.success) {
        this.$toast.success("Template deleted");
        this.selectedTemplate = null;
        await this.loadTemplates();
      } else {
        this.$toast.error("Failed to delete template");
      }
    },
    async addSection() {
      if (!this.newSection.title.trim()) return;
      this.addingSection = true;
      try {
        const res = await apiClient.addTemplateSection(this.selectedTemplate.id, this.newSection);
        if (res.success) {
          this.selectedTemplate.sections.push(res.data);
          this.newSection = { title: '', placeholder_text: '' };
          await this.loadTemplates();
          const updated = this.templates.find(t => t.id === this.selectedTemplate.id);
          if (updated) this.selectedTemplate = updated;
        } else {
          this.$toast.error(res.message || "Failed to add section");
        }
      } finally {
        this.addingSection = false;
      }
    },
    async removeSection(section) {
      const res = await apiClient.removeTemplateSection(this.selectedTemplate.id, section.id);
      if (res.success) {
        this.selectedTemplate.sections = this.selectedTemplate.sections.filter(s => s.id !== section.id);
        await this.loadTemplates();
        const updated = this.templates.find(t => t.id === this.selectedTemplate.id);
        if (updated) this.selectedTemplate = updated;
      } else {
        this.$toast.error("Failed to remove section");
      }
    },
  },
};
</script>
